/* eslint-disable indent */
/**
 *
 * Table
 *
 */

import React, { memo, useEffect, useState } from 'react';
import {
  useTable,
  usePagination,
  useSortBy,
  useRowSelect,
  useResizeColumns,
  useFlexLayout,
  useColumnOrder,
} from 'react-table';
import PropTypes from 'prop-types';
import NoData from '../NoData';
import Pagination from '../Pagination';
import { TableTextSkeleton } from '../Skeleton/library';
import {
  StyledTableWrapper,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableRow,
} from './Table.styles';
import { ColumnHeader, Toolbar } from './library';
import { StyledTableHeaderResizer } from './library/ColumnHeader.styles';
import {
  useTableFilters,
  useTableSearch,
  useTableSelection,
  useTableSettings,
} from './providers/TableContext';

function Table({
  pageIndexState,
  pageSizeState,
  setTableParams,
  columns,
  data,
  loading,
  skeleton,
  renderNoData,
  totalResults,
  fetchData,
  defaultSort,
  defaultFilters,
  toolbarActions,
  toolbarSearchField,
  toolbarFilters,
  toolbarFiltersList,
  hideToolbar,
  hidePagination,
  defaultPageSize = 20,
  hideTableSettings = false,
  hideFilters = false,
  onRowClick,
}) {
  // `data` is an optional prop with no default, and several slices initialise
  // their `data` to `null` rather than `[]`. So on the first render of a table
  // whose fetch has not been dispatched yet it is undefined or null — and
  // `loading` is still false at that point, so it gates nothing. Normalise once
  // here instead of guarding each read below.
  const rows = Array.isArray(data) ? data : [];

  const [skeletonTableRows, setSkeletonTableRows] = useState([]);

  const {
    isReady,
    setCurrentColumns,
    setTableContext,
    currentColumnOrder,
    currentHiddenColumns,
  } = useTableSettings();

  const { filters, setOptionFilters, setQueryFilter } = useTableFilters();

  const { setSelectedRows } = useTableSelection();

  const { selectedSearchValue } = useTableSearch();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setSortBy,
    toggleAllPageRowsSelected,
    state: {
      sortBy,
      selectedRowIds,
      // eslint-disable-next-line no-unused-vars
      columnOrder,
      // eslint-disable-next-line no-unused-vars
      hiddenColumns,
    },
    setColumnOrder,
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data: loading ? skeletonTableRows : rows,
      manualSortBy: true,
      enableRowSelection: true,
      manualPagination: true,
      autoResetPage: false,
      // TODO - for some reason, that need to be defined in react-table init
      // Math.ceil(totalResults / pageSize)
      pageCount: -1,
      initialState: {
        // https://github.com/TanStack/table/issues/411#issuecomment-604127940
        pageIndex: pageIndexState || 1,
        pageSize: defaultPageSize,
        columnOrder: currentColumnOrder,
        hiddenColumns: currentHiddenColumns,
        ...(defaultSort && {
          sortBy: [defaultSort],
        }),
      },
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    useColumnOrder,
    useResizeColumns,
  );

  useEffect(() => {
    setSelectedRows(Object.keys(selectedRowIds).map((index) => rows[index]));
  }, [selectedRowIds]);

  // Pass react-table context method to useTableSettings hook to get access that methods outside
  useEffect(() => {
    setTableContext({
      setColumnOrder,
      setHiddenColumns,
      toggleAllPageRowsSelected,
    });

    // Set default filters
    if (defaultFilters) {
      // eslint-disable-next-line no-unused-vars,no-restricted-syntax
      for (const filter of Object.keys(defaultFilters)) {
        if (filter === 'queryFilter') {
          setQueryFilter(defaultFilters.queryFilter);
        } else {
          setOptionFilters({ [filter]: defaultFilters[filter] });
        }
      }
    }
  }, []);

  useEffect(() => {
    // Set skeleton table rows based on selected pageSize to create better feeling for user
    setSkeletonTableRows(Array(pageSizeState).fill({}));
  }, [pageSizeState]);

  useEffect(() => {
    setCurrentColumns(columns);
  }, [columns]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('Table props changed:');
    // TODO - If anything else need to fired api call, add below
    // TODO - Make sure that `selectedRowIds ` should affect fetch process
    // We are handling sortBy only for single column
    fetchData({
      pageSize: pageSizeState,
      pageIndex: pageIndexState || 1,
      sortBy: sortBy.length ? sortBy[0] : {},
      filters,
      search: selectedSearchValue,
    });
  }, [sortBy, pageSizeState, pageIndexState, filters, selectedSearchValue]);

  if (!data) {
    console.error('Table expect `data` to be an array.');
  }

  const isRowSelected = (index) =>
    Object.keys(selectedRowIds).includes(index.toString());

  const RenderedNoData = () => {
    if (renderNoData) {
      return renderNoData();
    }
    return <NoData />;
  };

  const handleCellRender = (isLoading, cell) => {
    if (isLoading) return skeleton?.[cell.column?.id] ?? <TableTextSkeleton />;
    return cell.render('Cell');
  };
  return (
    <>
      {!hideToolbar && (
        <Toolbar
          toolbarActions={toolbarActions}
          toolbarSearchField={toolbarSearchField}
          toolbarFilters={toolbarFilters}
          toolbarFiltersList={toolbarFiltersList}
          hideTableSettings={hideTableSettings}
          hideFilters={hideFilters}
        />
      )}
      {!loading && !rows.length ? (
        <RenderedNoData />
      ) : (
        <>
          <StyledTableWrapper>
            {isReady && (
              <StyledTable {...getTableProps()}>
                <StyledTableHead>
                  {headerGroups.map((headerGroup) => (
                    <StyledTableRow {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(
                        (column) =>
                          !column.hidden && (
                            <StyledTableCell
                              {...column.getHeaderProps(
                                column.getSortByToggleProps(),
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ColumnHeader
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                column={column}
                              >
                                {column.render('Header')}
                                {column.resizableColumn && (
                                  <StyledTableHeaderResizer
                                    {...column.getResizerProps()}
                                  />
                                )}
                              </ColumnHeader>
                            </StyledTableCell>
                          ),
                      )}
                    </StyledTableRow>
                  ))}
                </StyledTableHead>
                <StyledTableBody {...getTableBodyProps()}>
                  {page.map((row, index) => {
                    prepareRow(row);
                    return (
                      <StyledTableRow
                        $isRowSelected={isRowSelected(index)}
                        $hasRowClick={!!onRowClick}
                        onClick={() => onRowClick?.(row.original, index)}
                        {...row.getRowProps()}
                      >
                        {row.cells.map((cell) => (
                          <StyledTableCell {...cell.getCellProps()}>
                            {handleCellRender(loading, cell)}
                          </StyledTableCell>
                        ))}
                      </StyledTableRow>
                    );
                  })}
                </StyledTableBody>
              </StyledTable>
            )}
          </StyledTableWrapper>
          {!hidePagination && (
            <Pagination
              disabled={loading}
              page={pageIndexState || 1}
              resultsPerPage={pageSizeState}
              total={totalResults}
              onPageChange={(updatedPage) => {
                setTableParams(updatedPage, pageSizeState);
              }}
              onResultsPerPageChange={(updatedResultsPerPage) => {
                setTableParams(1, updatedResultsPerPage);
                // That approach was copied from previous view
                // If page size will be changed, reset index to 1, if need to be changed
                // Do changes in `Pagination/index` component
              }}
            />
          )}
        </>
      )}
    </>
  );
}

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  defaultPageSize: PropTypes.number,
  hidePagination: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  loading: PropTypes.bool,
  fetchData: PropTypes.func,
  renderNoData: PropTypes.func,
  skeleton: PropTypes.object,
  totalResults: PropTypes.number,
  defaultSort: PropTypes.object,
  defaultFilters: PropTypes.object,
  toolbarActions: PropTypes.array,
  toolbarSearchField: PropTypes.func,
  toolbarFilters: PropTypes.node,
  toolbarFiltersList: PropTypes.node,
  hideTableSettings: PropTypes.bool,
  hideFilters: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  onRowClick: PropTypes.func,
  handleCellClick: PropTypes.func,
  editedValue: PropTypes.string,
  handleSave: PropTypes.func,
  isEditing: PropTypes.func,
  pageIndexState: PropTypes.number,
  pageSizeState: PropTypes.number,
  setTableParams: PropTypes.func,
};

export {
  Text,
  Status,
  Date,
  User,
  Checkbox,
  ActionMenu,
  YesNo,
  SimpleList,
  SimpleCount,
  Dropdown,
  BadgeList,
  LoginStatus,
  Number,
  FormattedDateTime,
} from './library';

export { TableProvider } from './providers/TableContext';

export default memo(Table);
